<p-toast key="preoffer-settings" position="top-right"></p-toast>

<p-card styleClass="validation-card">
  <div class="page-header">
    <div>
      <h1>Preoferta automática</h1>
      <p class="header-subtitle" *ngIf="overview">
        Versión {{ overview.active.version ?? 'semilla' }} ·
        <span class="mode-pill" [ngClass]="'is-' + modeUi[overview.active.mode].tone" data-test="settings-mode">{{
          modeUi[overview.active.mode].label
        }}</span>
      </p>
    </div>
  </div>

  <p-divider></p-divider>

  <div class="empty-state" *ngIf="loading && !overview" data-test="settings-loading">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
    <span>Cargando la configuración…</span>
  </div>

  <div class="empty-state is-error" *ngIf="loadError" role="alert" data-test="settings-error">
    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
    <h3>No pudimos cargar la configuración</h3>
    <span>{{ loadError }}</span>
    <button pButton type="button" class="p-button-outlined p-button-sm" icon="pi pi-refresh" label="Reintentar" (click)="load()"></button>
  </div>

  <div class="settings-grid" *ngIf="overview && form && !loadError">
    <section class="block" aria-labelledby="mode-title">
      <h2 id="mode-title">Modo</h2>
      <p class="note">{{ modeUi[overview.active.mode].text }}</p>
      <p class="note is-info" *ngIf="overview.active.mode === 'shadow'" data-test="settings-would-have">
        <i class="pi pi-eye" aria-hidden="true"></i>
        Hoy {{ overview.wouldHaveIssued }}
        {{ overview.wouldHaveIssued === 1 ? 'preoferta vigente estaría' : 'preofertas vigentes estarían' }} en «habría salido».
      </p>

      <ng-container *ngIf="canActivate; else modeReadonly">
        <p-selectButton
          [options]="modeOptions"
          optionLabel="label"
          optionValue="value"
          [ngModel]="modeTarget"
          (ngModelChange)="selectMode($event)"
          [disabled]="savingMode"
          ariaLabelledBy="mode-title"
          data-test="settings-mode-select"
        ></p-selectButton>

        <div class="mode-change" *ngIf="modeChanging" data-test="settings-mode-change">
          <p class="note">{{ modeUi[modeTarget].text }}</p>
          <label for="mode-reason">Motivo del cambio</label>
          <textarea id="mode-reason" pInputTextarea rows="3" [(ngModel)]="modeReason" data-test="settings-mode-reason"></textarea>
          <small class="hint">Al menos {{ limits.reason }} caracteres. Queda en el historial.</small>

          <div class="ack" *ngIf="modeTarget === 'live'" data-test="settings-ack">
            <p-checkbox [(ngModel)]="acknowledged" [binary]="true" inputId="ack-live"></p-checkbox>
            <label for="ack-live">
              Entiendo que desde ahora se enviarán correos a clientes elegibles; hoy {{ overview.wouldHaveIssued }}
              {{ overview.wouldHaveIssued === 1 ? 'estaría' : 'estarían' }} en «habría salido». Encender no envía nada de forma retroactiva.
            </label>
          </div>

          <div class="actions">
            <button
              pButton
              type="button"
              label="Cancelar"
              class="p-button-text"
              [disabled]="savingMode"
              (click)="selectMode(overview.active.mode)"
            ></button>
            <button
              pButton
              type="button"
              icon="pi pi-power-off"
              [label]="'Cambiar a ' + modeUi[modeTarget].label.toLowerCase()"
              [disabled]="savingMode || !modeReady"
              (click)="saveMode()"
              data-test="settings-mode-save"
            ></button>
          </div>
        </div>
      </ng-container>
      <ng-template #modeReadonly>
        <p class="note" data-test="settings-mode-readonly">
          <i class="pi pi-lock" aria-hidden="true"></i>
          No tienes permiso para cambiar el modo.
        </p>
      </ng-template>
    </section>

    <section class="block" aria-labelledby="thresholds-title">
      <h2 id="thresholds-title">Quién entra</h2>
      <p class="note">
        Entran los clientes nuevos de cualquier canal al completar su CIEC, con su calculadora y su oferta conservadora. Guardar crea una
        versión nueva con el mismo modo: las evaluaciones que sigan la usan; las ya decididas no cambian.
      </p>

      <div class="fields">
        <label class="field">
          <span class="field-label">Tope del monto preaprobado (con IVA)</span>
          <p-inputNumber
            [(ngModel)]="form.maxPreapprovedAmount"
            mode="currency"
            currency="MXN"
            locale="es-MX"
            [min]="limits.maxPreapprovedAmount.min"
            [max]="limits.maxPreapprovedAmount.max"
            [disabled]="!canUpdate"
            inputId="max-amount"
          ></p-inputNumber>
          <small class="hint">Si la oferta conservadora lo rebasa, la carta sale por el tope y se avisa al comercial.</small>
        </label>
        <label class="field">
          <span class="field-label">Puntaje mínimo (de 110)</span>
          <p-inputNumber
            [(ngModel)]="form.minTotalScore"
            [min]="limits.minTotalScore.min"
            [max]="limits.minTotalScore.max"
            [maxFractionDigits]="2"
            [disabled]="!canUpdate"
            inputId="min-score"
          ></p-inputNumber>
        </label>
        <label class="field">
          <span class="field-label">Vigencia de la carta (días)</span>
          <p-inputNumber
            [(ngModel)]="form.validityDays"
            [min]="limits.validityDays.min"
            [max]="limits.validityDays.max"
            [useGrouping]="false"
            [disabled]="!canUpdate"
            inputId="validity"
          ></p-inputNumber>
        </label>
        <fieldset class="field is-wide">
          <legend class="field-label">Tipos de persona</legend>
          <div class="checks">
            <span class="check" *ngFor="let option of personTypeOptions">
              <p-checkbox
                name="person-types"
                [value]="option.value"
                [(ngModel)]="form.personTypes"
                [inputId]="'person-' + option.value"
                [disabled]="!canUpdate"
              ></p-checkbox>
              <label [for]="'person-' + option.value">{{ option.label }}</label>
            </span>
          </div>
        </fieldset>
      </div>

      <ng-container *ngIf="canUpdate; else thresholdsReadonly">
        <div class="mode-change" *ngIf="thresholdsDirty" data-test="settings-thresholds-change">
          <label for="thresholds-reason">Motivo del cambio</label>
          <textarea
            id="thresholds-reason"
            pInputTextarea
            rows="3"
            [(ngModel)]="thresholdsReason"
            data-test="settings-thresholds-reason"
          ></textarea>
          <small class="hint">Al menos {{ limits.reason }} caracteres. Queda en el historial.</small>
        </div>
        <ul class="errors" *ngIf="showThresholdErrors && thresholdErrors.length" role="alert" data-test="settings-thresholds-errors">
          <li *ngFor="let error of thresholdErrors">{{ error }}</li>
        </ul>
        <div class="actions">
          <button
            pButton
            type="button"
            label="Descartar"
            class="p-button-text"
            [disabled]="savingThresholds || !thresholdsDirty"
            (click)="resetThresholds()"
          ></button>
          <button
            pButton
            type="button"
            icon="pi pi-save"
            label="Guardar umbrales"
            [disabled]="savingThresholds || !thresholdsDirty"
            (click)="saveThresholds()"
            data-test="settings-thresholds-save"
          ></button>
        </div>
      </ng-container>
      <ng-template #thresholdsReadonly>
        <p class="note" data-test="settings-thresholds-readonly">
          <i class="pi pi-lock" aria-hidden="true"></i>
          No tienes permiso para cambiar los umbrales.
        </p>
      </ng-template>
    </section>

    <section class="block is-wide" aria-labelledby="history-title">
      <h2 id="history-title">Historial</h2>
      <p class="muted" *ngIf="!overview.history.length" data-test="settings-history-empty">
        Sin versiones guardadas: rige la semilla (sombra, tope $3,500,000, puntaje 60).
      </p>
      <ol class="history" *ngIf="overview.history.length" data-test="settings-history">
        <li *ngFor="let item of overview.history; trackBy: trackByVersion" [class.is-active]="item.status === 'active'">
          <div class="history-head">
            <strong>v{{ item.version }}</strong>
            <span class="mode-pill" [ngClass]="'is-' + modeUi[item.mode].tone">{{ modeUi[item.mode].label }}</span>
            <span class="muted" *ngIf="item.status === 'active'">vigente</span>
            <span class="muted">{{ item.createdAt | mxDate }} · {{ item.changedBy || 'Sin autor' }}</span>
          </div>
          <p class="history-summary">{{ versionSummary(item) }}</p>
          <p class="history-reason" *ngIf="item.changeReason">«{{ item.changeReason }}»</p>
        </li>
      </ol>
    </section>
  </div>
</p-card>
